import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import resourcesToBackend from "i18next-resources-to-backend";
import { Settings } from "luxon";
import type { Locale } from "date-fns";

import { en, NAMESPACES } from "../locales/en";
import { PSEUDO_LANGUAGE, startupLanguage } from "./languages";
import { numberFormatter } from "./numberFormatter";
import { pseudoLocalise } from "./pseudo";

const loadBundle = (language: string, namespace: string) =>
  language === PSEUDO_LANGUAGE ? Promise.resolve({}) : import(`../locales/${language}/${namespace}.json`);

const DATE_FNS_LOCALES: Record<string, () => Promise<Locale>> = {
  es: () => import("date-fns/locale/es").then(module => module.es),
  fr: () => import("date-fns/locale/fr").then(module => module.fr),
  th: () => import("date-fns/locale/th").then(module => module.th),
};

// Note: Intl writes Thai dates in the Buddhist era (2569) unless the Gregorian calendar is asked for.
export const luxonLocale = (language: string) => (language === "th" ? "th-TH-u-ca-gregory" : language);

async function applyDateLocale(language: string) {
  const loadDateFnsLocale = DATE_FNS_LOCALES[language];
  if (!loadDateFnsLocale) return;

  Settings.defaultLocale = luxonLocale(language);
  const [{ registerLocale, setDefaultLocale }, locale] = await Promise.all([
    import("react-datepicker"),
    loadDateFnsLocale(),
  ]);
  registerLocale(language, locale);
  setDefaultLocale(language);
}

export async function initI18n(language = startupLanguage()) {
  await i18n
    .use(initReactI18next)
    .use(resourcesToBackend(loadBundle))
    .use({ type: "postProcessor", name: "pseudo", process: (value: string) => pseudoLocalise(value) })
    .use(numberFormatter)
    .init({
      lng: language,
      fallbackLng: "en",
      ns: NAMESPACES,
      defaultNS: "common",
      resources: { en },
      partialBundledLanguages: true,
      initAsync: false,
      interpolation: { escapeValue: false, alwaysFormat: true },
      postProcess: language === PSEUDO_LANGUAGE ? ["pseudo"] : false,
      returnEmptyString: false,
    });

  if (typeof document !== "undefined") document.documentElement.lang = language === PSEUDO_LANGUAGE ? "en" : language;
  await applyDateLocale(language);
  return i18n;
}

export const currentLanguage = () => (i18n.language === PSEUDO_LANGUAGE ? "en" : i18n.language ?? "en");

export default i18n;
